// ─── The chain the whole app runs against ───────────────────────────────
//
// Admins pick this from the top-bar switch (NetworkSwitcher); everyone else
// gets VITE_POLYGON_CHAIN_ID. Deliberately dependency-free -- contracts.ts,
// privy.ts and useNetworkStore all import it, so anything it imported back
// would be a cycle. That also makes it the right home for the canonical list
// of chains the app supports: contracts.ts keys CHAIN_CONFIGS off these ids,
// and useNetworkStore/NetworkSwitcher render straight from this list, so a
// new chain is added here once rather than in four places.
//
// The value is read from localStorage ONCE, at module load, and frozen for
// the life of the page. Switching networks writes the new id and reloads, so
// every module -- the Privy provider's defaultChain, PolygonClient, the
// react-query caches -- re-initialises together instead of half the app
// pointing at Amoy while the other half talks to Polygon.

export const AMOY_CHAIN_ID = 80002;
export const POLYGON_CHAIN_ID = 137;

export const STORED_CHAIN_KEY = "wexare-active-chain";

export interface SupportedChain {
  id: number;
  /** Real funds move here. Drives the switch's confirmation prompt. */
  isMainnet: boolean;
  labelKey: string;
  fallback: string;
  shortKey: string;
  shortFallback: string;
  dotClass: string;
}

/**
 * Every chain the app can run against. The single source for "is this id ok?".
 * Typed non-empty so callers can fall back to SUPPORTED_CHAINS[0] without a
 * null check -- the app is never configured with zero chains.
 */
export const SUPPORTED_CHAINS: readonly [SupportedChain, ...SupportedChain[]] = [
  {
    id: AMOY_CHAIN_ID,
    isMainnet: false,
    labelKey: "network.testnet",
    fallback: "Testnet (Amoy)",
    shortKey: "network.testnetShort",
    shortFallback: "Testnet",
    dotClass: "bg-amber-400",
  },
  {
    id: POLYGON_CHAIN_ID,
    isMainnet: true,
    labelKey: "network.mainnet",
    fallback: "Mainnet (Polygon)",
    shortKey: "network.mainnetShort",
    shortFallback: "Mainnet",
    dotClass: "bg-emerald-500",
  },
];

export function isSupportedChainId(value: unknown): value is number {
  return SUPPORTED_CHAINS.some((chain) => chain.id === value);
}

export function getSupportedChain(chainId: number): SupportedChain | undefined {
  return SUPPORTED_CHAINS.find((chain) => chain.id === chainId);
}

/** True when the given chain moves real funds. */
export function isMainnetChainId(chainId: number): boolean {
  return getSupportedChain(chainId)?.isMainnet ?? false;
}

function envChainId(): number {
  const raw = import.meta.env.VITE_POLYGON_CHAIN_ID;
  const fromEnv = Number(raw);
  if (isSupportedChainId(fromEnv)) return fromEnv;
  // A typo'd or unsupported VITE_POLYGON_CHAIN_ID would otherwise start the
  // whole app on testnet without a word. Say so loudly in dev; still fall back
  // so a bad env var can't white-screen production.
  if (raw !== undefined && raw !== "") {
    console.error(
      `[activeChain] VITE_POLYGON_CHAIN_ID="${String(raw)}" is not a supported chain; falling back to Amoy (${AMOY_CHAIN_ID}).`,
    );
  }
  return AMOY_CHAIN_ID;
}

function readUrlChainId(): number | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("chain");
    if (raw == null) return null;
    const id = Number(raw);
    if (!isSupportedChainId(id)) return null;
    // Best-effort persist so navigations without the param stay on this chain.
    // Some domains block localStorage, so the param is kept in the URL as the
    // primary source of truth.
    try {
      localStorage.setItem(STORED_CHAIN_KEY, String(id));
    } catch { /* best-effort */ }
    return id;
  } catch {
    return null;
  }
}

function readStoredChainId(): number | null {
  try {
    const stored = Number(localStorage.getItem(STORED_CHAIN_KEY));
    return isSupportedChainId(stored) ? stored : null;
  } catch {
    /* SSR / restricted storage */
    return null;
  }
}

const ACTIVE_CHAIN_ID = readUrlChainId() ?? readStoredChainId() ?? envChainId();

/** The chain every read, write and transaction in this page load targets. */
export function getActiveChainId(): number {
  return ACTIVE_CHAIN_ID;
}

/** The chain this deployment defaults to, ignoring any stored override. */
export function getEnvChainId(): number {
  return envChainId();
}

/** True when the selection came from the switch rather than the env default. */
export function isChainOverridden(): boolean {
  return ACTIVE_CHAIN_ID !== envChainId();
}

/**
 * Persist a new chain. Callers are expected to reload right after -- this does
 * NOT change getActiveChainId() for the current page load, by design.
 *
 * Returns false when the id is unsupported or storage refused the write (a
 * full quota, a locked-down browser). Callers must not reload on false: the
 * page would come back on the same chain and the switch would look broken.
 */
export function storeActiveChainId(chainId: number): boolean {
  if (!isSupportedChainId(chainId)) return false;
  try {
    localStorage.setItem(STORED_CHAIN_KEY, String(chainId));
    return true;
  } catch {
    /* SSR / restricted storage */
    return false;
  }
}

/** Drop any stored override so the next page load uses the env default. */
export function clearActiveChainId(): void {
  try {
    localStorage.removeItem(STORED_CHAIN_KEY);
  } catch {
    /* SSR / restricted storage */
  }
}
